import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useContent, C } from "@/hooks/useContent";
import { useSections, visible } from "@/hooks/useSections";
import { Menu, X } from "lucide-react";
import { useState, useEffect } from "react";

const navItems = [
  { to: "/", label: "Home" },
  { to: "/portfolio", label: "Portfolio" },
  { to: "/about", label: "About" },
  { to: "/booking", label: "Book" },
];

export default function Layout() {
  const content = useContent();
  const sections = useSections();
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [location.pathname]);

  return (
    <div className="min-h-screen flex flex-col">
      <header className="glass-nav sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <Link to="/" data-testid="nav-logo" className="flex items-center group">
            <div className="flex flex-col leading-none">
              <span className="font-display text-lg font-semibold tracking-tight">
                {C(content, "business.brand_top", "Northwestern")}
              </span>
              <span className="text-[10px] tracking-[0.22em] uppercase text-muted-foreground mt-1">
                {C(content, "business.brand_bottom", "EXTERIOR CLEANING")}
              </span>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-10">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `text-sm font-medium transition-colors ${
                    isActive ? "text-primary" : "text-muted-foreground hover:text-foreground"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <Link to="/booking" className="hidden md:inline-flex">
              <Button data-testid="nav-cta-book" className="btn-primary rounded-full px-5">
                {C(content, "nav.cta", "Book a free estimate")}
              </Button>
            </Link>
            <button
              className="md:hidden p-2"
              onClick={() => setOpen((s) => !s)}
              data-testid="nav-mobile-toggle"
              aria-label="menu"
            >
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {open && (
          <div className="md:hidden border-t border-border bg-background/95 backdrop-blur">
            <div className="px-6 py-4 flex flex-col gap-3">
              {navItems.map((item) => (
                <NavLink key={item.to} to={item.to} data-testid={`nav-mobile-${item.label.toLowerCase()}`} className="py-2 text-sm font-medium">
                  {item.label}
                </NavLink>
              ))}
              <Link to="/booking">
                <Button data-testid="nav-mobile-cta" className="btn-primary w-full rounded-full">
                  {C(content, "nav.cta", "Book a free estimate")}
                </Button>
              </Link>
            </div>
          </div>
        )}
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      <footer className="bg-primary text-primary-foreground mt-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14 lg:py-16">
          <div className="grid md:grid-cols-3 gap-10">
            <div>
              <div className="mb-5">
                <div className="font-display text-xl font-semibold leading-tight">
                  {C(content, "business.brand_top", "Northwestern")}
                </div>
                <div className="text-[10px] tracking-[0.22em] uppercase text-white/60 mt-1">
                  {C(content, "business.brand_bottom", "EXTERIOR CLEANING")}
                </div>
              </div>
              <p className="text-white/70 text-sm max-w-xs leading-relaxed" data-testid="footer-tagline" style={{display: visible(sections, "footer.tagline") ? undefined : "none"}}>
                {C(content, "footer.tagline", "Concrete, restored. Locally owned. Serving North Seattle, WA.")}
              </p>
            </div>
            <div>
              <p className="overline text-white/50 mb-4">Contact</p>
              <a
                href={`mailto:${C(content, "business.email", "mberg@northwesternexteriorcleaning.com")}`}
                data-testid="footer-email"
                className="text-sm text-white hover:underline break-all"
              >
                {C(content, "business.email", "mberg@northwesternexteriorcleaning.com")}
              </a>
              <p className="text-sm text-white/70 mt-3">{C(content, "business.area", "North Seattle, WA")}</p>
              {visible(sections, "footer.hours") && (
                <div className="mt-3" data-testid="footer-hours">
                  <p className="overline text-white/50 mb-1">{C(content, "footer.hours_label", "Hours")}</p>
                  <p className="text-sm text-white/85" data-testid="footer-hours-value">{C(content, "footer.hours_value", "Mon–Sat · 1 PM – 7 PM")}</p>
                </div>
              )}
            </div>
            <div>
              <p className="overline text-white/50 mb-4">Quick links</p>
              <div className="flex flex-col gap-2 text-sm">
                {navItems.map((n) => (
                  <Link key={n.to} to={n.to} className="text-white/80 hover:text-white">
                    {n.label}
                  </Link>
                ))}
                <Link to="/admin" className="text-white/40 hover:text-white text-xs mt-3" data-testid="footer-admin-link">
                  Owner dashboard
                </Link>
              </div>
            </div>
          </div>
          <div className="mt-10 pt-6 border-t border-white/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
            <p className="text-xs text-white/50">© {new Date().getFullYear()} {C(content, "business.name", "Northwestern Exterior Cleaning")}. All rights reserved.</p>
            <p className="text-xs text-white/40">{C(content, "footer.signoff", "Built for the Pacific Northwest ☔︎")}</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
